import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ProgressRing, getProgressStage } from '../components/ui/progress-ring';
import { BelongingFooter } from '../components/ui/belonging-footer';
import { EcosystemNav } from '../components/ui/ecosystem-nav';
import { toast } from 'sonner';
import { 
  AddIcon, CommunityIcon, ArrowIcon, SeedIcon, BelongingIcon 
} from '../components/ui/icons';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [userName, setUserName] = useState('');
  const [personalStatement, setPersonalStatement] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [joinProjectId, setJoinProjectId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showJoinForm, setShowJoinForm] = useState(false);

  useEffect(() => {
    const savedName = localStorage.getItem('blissabode_user_name');
    const savedStatement = localStorage.getItem('blissabode_personal_statement');
    if (savedName) {
      setUserName(savedName);
      if (savedStatement) setPersonalStatement(savedStatement);
      loadProjects(savedName);
    } else {
      setIsLoading(false);
    }
  }, []);

  const loadProjects = async (name) => {
    try {
      const response = await fetch(`${API_URL}/api/projects?user_name=${encodeURIComponent(name)}`);
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetName = (e) => {
    e.preventDefault();
    if (!userName.trim()) return;
    localStorage.setItem('blissabode_user_name', userName.trim());
    if (personalStatement.trim()) {
      localStorage.setItem('blissabode_personal_statement', personalStatement.trim());
    }
    loadProjects(userName.trim());
    toast.success(`Welcome, ${userName}!`);
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim()) return;

    try {
      const response = await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newProjectName.trim(),
          creator_name: userName,
        }),
      });

      if (!response.ok) throw new Error('Failed to create project');

      const data = await response.json();
      toast.success('Charter created! Your compass journey begins.');
      navigate(`/compass/${data.id}`);
    } catch (error) {
      console.error('Create error:', error);
      toast.error('Failed to create project');
    }
  };

  const handleJoinProject = async (e) => {
    e.preventDefault();
    if (!joinCode.trim() || !joinProjectId.trim()) {
      toast.error('Please enter both Project ID and Join Code');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/projects/${joinProjectId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          join_code: joinCode.trim().toUpperCase(),
          user_name: userName,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to join');
      }

      toast.success('Joined successfully!');
      navigate(`/compass/${joinProjectId}`);
    } catch (error) {
      console.error('Join error:', error);
      toast.error(error.message || 'Failed to join project');
    }
  };

  // Welcome screen
  if (!localStorage.getItem('blissabode_user_name')) {
    return (
      <div className="min-h-screen bg-[var(--bg)] flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-lg w-full"
          >
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring' }}
                className="w-20 h-20 mx-auto mb-6 rounded-full bg-[var(--primary)] flex items-center justify-center"
              >
                <SeedIcon size={40} className="text-white" />
              </motion.div>
              <h1 className="font-heading text-4xl text-[var(--fg)] mb-3">Welcome to Bliss Abode</h1>
              <p className="text-[var(--fg)]/70 max-w-sm mx-auto">
                Craft your community's founding charter together. Build architecture that lasts for generations.
              </p>
              <p className="text-xs text-[var(--primary)] mt-2">
                Part of the Belonging Sessions Family
              </p>
            </div>

            <Card>
              <CardContent className="pt-6">
                <form onSubmit={handleSetName} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-[var(--fg)] mb-2 block">
                      What should we call you?
                    </label>
                    <Input
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      placeholder="Your name"
                      data-testid="welcome-name-input"
                    />
                  </div>
                  
                  {/* Personal Statement - Optional */}
                  <div>
                    <label className="text-sm font-medium text-[var(--fg)] mb-1 block">
                      Your Personal Statement <span className="text-[var(--fg)]/40 font-normal">(optional)</span>
                    </label>
                    <p className="text-xs text-[var(--fg)]/50 mb-2">
                      Share your essence: What are your deepest desires and needs? What have you accomplished? 
                      What do you love most about life and living?
                    </p>
                    <Textarea
                      value={personalStatement}
                      onChange={(e) => setPersonalStatement(e.target.value)}
                      placeholder="I am someone who values... My greatest accomplishments include... What I love most about life is... My vision for community is..."
                      className="min-h-[120px] text-sm"
                      data-testid="welcome-personal-statement"
                    />
                    <p className="text-xs text-[var(--fg)]/40 mt-1 italic">
                      This will be added to your charter's Soul & Signal section
                    </p>
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={!userName.trim()}
                    data-testid="welcome-continue-button"
                  >
                    Begin the Journey
                    <ArrowIcon size={16} />
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="mt-8 text-center">
              <div className="flex justify-center gap-2 mb-3">
                {['Belonging', 'Agency', 'Security', 'Trust'].map((pillar) => (
                  <span 
                    key={pillar}
                    className="text-xs px-2 py-0.5 rounded-full bg-[var(--sky)] text-[var(--fg)]/60"
                  >
                    {pillar}
                  </span>
                ))}
              </div>
              <p className="text-xs text-[var(--fg)]/40 italic">
                "planetary confluence through shared remembrance"
              </p>
            </div>
          </motion.div>
        </div>
        <BelongingFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg)] flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur bg-[var(--bg)]/90 border-b border-[var(--muted)]/50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[var(--primary)] flex items-center justify-center">
              <SeedIcon size={20} className="text-white" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-semibold text-[var(--fg)]">Bliss Abode</h1>
              <p className="text-xs text-[var(--fg)]/60">Belonging Sessions Family</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[var(--fg)]/70">Hello, {userName}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                localStorage.removeItem('blissabode_user_name');
                window.location.reload();
              }}
              data-testid="logout-button"
            >
              Change Name
            </Button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-3xl md:text-4xl text-[var(--fg)] mb-3">
            Craft Your Community Charter
          </h2>
          <p className="text-[var(--fg)]/70 max-w-xl mx-auto">
            Create the architecture for a thriving community.
            M.ai.ry will guide you through 8 essential foundations.
          </p>
        </motion.div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          <Button
            size="lg"
            onClick={() => setShowCreateForm(!showCreateForm)}
            data-testid="create-project-button"
          >
            <AddIcon size={20} />
            New Charter
          </Button>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => setShowJoinForm(!showJoinForm)}
            data-testid="join-project-button"
          >
            <CommunityIcon size={20} />
            Join Charter
          </Button>
        </div>

        {/* Create form */}
        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="max-w-md mx-auto mb-8"
          >
            <Card>
              <CardHeader>
                <CardTitle>Create New Charter</CardTitle>
                <CardDescription>Start building your community's foundation</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateProject} className="space-y-4">
                  <Input
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    placeholder="Charter name (e.g., Sunrise Community)"
                    data-testid="new-project-name-input"
                  />
                  <div className="flex gap-2">
                    <Button type="submit" disabled={!newProjectName.trim()} data-testid="create-project-submit">
                      Create Charter
                    </Button>
                    <Button variant="ghost" onClick={() => setShowCreateForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Join form */}
        {showJoinForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="max-w-md mx-auto mb-8"
          >
            <Card>
              <CardHeader>
                <CardTitle>Join Existing Charter</CardTitle>
                <CardDescription>Enter the project ID and join code shared with you</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleJoinProject} className="space-y-4">
                  <Input
                    value={joinProjectId}
                    onChange={(e) => setJoinProjectId(e.target.value)}
                    placeholder="Project ID"
                    data-testid="join-project-id-input"
                  />
                  <Input
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                    placeholder="Join Code (e.g., ABC123)"
                    className="font-mono tracking-widest"
                    data-testid="join-code-input"
                  />
                  <div className="flex gap-2">
                    <Button type="submit" disabled={!joinCode.trim() || !joinProjectId.trim()} data-testid="join-project-submit">
                      Join Charter
                    </Button>
                    <Button variant="ghost" onClick={() => setShowJoinForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Projects grid */}
        {projects.length > 0 && (
          <div>
            <h3 className="font-heading text-xl text-[var(--fg)] mb-4">Your Charters</h3>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {projects.map((project, i) => {
                const progress = 0;
                const { stage, emoji } = getProgressStage(progress);
                
                return (
                  <motion.div
                    key={project._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <Card
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => navigate(`/compass/${project._id}`)}
                      data-testid="project-card"
                    >
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-heading text-lg text-[var(--fg)] mb-1">
                              {project.name}
                            </h4>
                            <p className="text-xs text-[var(--fg)]/60">
                              {project.members?.length || 1} member{project.members?.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <ProgressRing size={48} stroke={4} progress={progress} />
                        </div>
                        <div className="mt-4 flex items-center justify-between">
                          <span className="text-xs text-[var(--fg)]/50">
                            Created {new Date(project.created_at).toLocaleDateString()}
                          </span>
                          <span className="text-xs text-[var(--fg)]/60">
                            {stage} {emoji}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && projects.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-[var(--sky)] flex items-center justify-center">
              <BelongingIcon size={48} className="text-[var(--primary)]" />
            </div>
            <h3 className="font-heading text-xl text-[var(--fg)] mb-2">
              Plant Your First Seed
            </h3>
            <p className="text-sm text-[var(--fg)]/60 max-w-sm mx-auto">
              Create your first community charter and start building something that lasts for generations.
            </p>
          </motion.div>
        )}
      </main>

      <EcosystemNav currentSite="community-forge" />
    </div>
  );
}
